import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  EVAL_CODES,
  PROMPT_CODES,
  PROMPT_VARS,
  REASONING_EFFORTS,
  type ModelCheckResult,
  type PromptCode,
  type PromptDto,
  type PromptSummary,
  type ReasoningEffortSetting,
} from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Badge, Button, Card, Modal, Spinner, Textarea, Toast } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { useT } from '../../i18n';
import { api, type PromptSteps } from '../../lib/api';
import { cn } from '../../lib/cn';
import { errorText } from '../../lib/errors';
import { adminErrorText, formatDate } from '../../lib/format';

const inputClass =
  'w-full h-11 px-3 rounded-lg text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

const isPromptCode = (v: string | undefined): v is PromptCode => PROMPT_CODES.some((c) => c === v);
const isEvalCode = (code: PromptCode) => EVAL_CODES.some((c) => c === code);

/** Versions of one brain step: read, activate, compare, and write a new one (spec 7.4). */
export function AdminPromptStepPage() {
  const code = useParams().code;
  if (!isPromptCode(code)) return <Navigate to="/admin/prompts" replace />;
  return <StepScreen code={code} />;
}

function StepScreen({ code }: { code: PromptCode }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const list = useQuery<PromptSteps>({ queryKey: ['admin', 'prompts'], queryFn: () => api.adminListPrompts() });
  const step = list.data?.steps.find((s) => s.code === code);
  const active = step?.active ?? null;
  const versions = [...(step?.versions ?? [])].sort((a, b) => b.version - a.version);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState<PromptSummary | null>(null);
  const [activating, setActivating] = useState(false);

  const selected = versions.find((v) => v.id === selectedId) ?? null;
  const body = useQuery<PromptDto>({
    queryKey: ['admin', 'prompt', selectedId],
    queryFn: () => api.adminGetPrompt(selectedId!),
    enabled: selectedId !== null,
  });

  const activate = async (v: PromptSummary) => {
    setConfirmActivate(null);
    setActivating(true);
    try {
      await api.adminActivatePrompt(v.id);
      await qc.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      show(t.adminPrompts.activated, 'success');
    } catch (err) {
      show(adminErrorText(err, t), 'destructive');
    } finally {
      setActivating(false);
    }
  };

  const effortLabel = (e: ReasoningEffortSetting | null) => e ?? t.adminPrompts.effortNone;

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.adminPrompts.steps[code]} to="/admin/prompts" />

      {list.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {list.error && <p className="text-sm text-hint">{errorText(list.error, t)}</p>}

      {step && !editorOpen && (
        <Button variant="ink" fullWidth className="mb-4" onClick={() => setEditorOpen(true)}>
          {t.adminPrompts.newVersion}
        </Button>
      )}

      {step && editorOpen && list.data && (
        <PromptEditor
          code={code}
          base={active}
          vars={
            code === 'text_diagram'
              ? list.data.diagramVars
              : code === 'text_listicle'
                ? list.data.listicleVars
                : list.data.vars
          }
          onCancel={() => setEditorOpen(false)}
          onSaved={(p) => {
            setEditorOpen(false);
            setSelectedId(p.id);
            void qc.invalidateQueries({ queryKey: ['admin', 'prompts'] });
            show(t.adminPrompts.saved, 'success');
          }}
        />
      )}

      {step && (
        <>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-hint mb-2">{t.adminPrompts.versions}</h2>
          <ul className="space-y-2">
            {versions.map((v) => (
              <li key={v.id}>
                <Card
                  variant="surface"
                  padding="md"
                  interactive
                  onClick={() => setSelectedId(selectedId === v.id ? null : v.id)}
                  className={cn(selectedId === v.id && 'ring-2 ring-ink/40')}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold">{t.adminPrompts.version(v.version)}</span>
                    <span className="flex items-center gap-2">
                      {v.isActive && <Badge tone="success">{t.adminPrompts.active}</Badge>}
                      <span className="text-xs text-hint">{formatDate(v.createdAt)}</span>
                    </span>
                  </div>
                  <p className="text-xs text-hint break-all">
                    {v.model} · {t.adminPrompts.effort}: {effortLabel(v.reasoningEffort)}
                  </p>
                  {v.note && <p className="text-xs mt-1 break-words">{v.note}</p>}
                </Card>

                {selected?.id === v.id && (
                  <div className="mt-2 mb-3">
                    {body.isLoading && (
                      <div className="flex justify-center py-3">
                        <Spinner />
                      </div>
                    )}
                    {body.data && (
                      <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-surface-2 rounded-xl p-3 max-h-96 overflow-y-auto">
                        {body.data.body}
                      </pre>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {!v.isActive && (
                        <Button variant="ink" size="sm" loading={activating} onClick={() => setConfirmActivate(v)}>
                          {t.adminPrompts.activate}
                        </Button>
                      )}
                      {!v.isActive && active && isEvalCode(code) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/admin/eval/new?code=${code}&a=${active.id}&b=${v.id}`)}
                        >
                          {t.adminPrompts.compare}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <Modal open={confirmActivate !== null} onClose={() => setConfirmActivate(null)}>
        {confirmActivate && (
          <>
            <p className="text-sm mb-4">{t.adminPrompts.activateConfirm(confirmActivate.version)}</p>
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setConfirmActivate(null)}>
                {t.adminPrompts.cancel}
              </Button>
              <Button variant="ink" fullWidth onClick={() => activate(confirmActivate)}>
                {t.adminPrompts.activate}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {toast}
    </div>
  );
}

function PromptEditor({
  code,
  base,
  vars,
  onCancel,
  onSaved,
}: {
  code: PromptCode;
  base: PromptSummary | null;
  vars: PromptSteps['vars'];
  onCancel: () => void;
  onSaved: (p: PromptDto) => void;
}) {
  const t = useT();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const baseBody = useQuery<PromptDto>({
    queryKey: ['admin', 'prompt', base?.id ?? null],
    queryFn: () => api.adminGetPrompt(base!.id),
    enabled: base !== null,
  });
  const [body, setBody] = useState<string | null>(null);
  const [model, setModel] = useState(base?.model ?? '');
  const [effort, setEffort] = useState<ReasoningEffortSetting | null>(
    code === 'transcribe' ? null : (base?.reasoningEffort ?? null),
  );
  const [note, setNote] = useState('');
  const [check, setCheck] = useState<ModelCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The text starts as the working version's once it arrives.
  const text = body ?? baseBody.data?.body ?? '';

  const insert = (name: string) => {
    const token = `{{${name}}}`;
    const el = bodyRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setBody(text.slice(0, start) + token + text.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const checkModel = async () => {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await api.adminCheckModel({ code, model: model.trim(), reasoningEffort: effort }));
    } catch {
      setCheck('unavailable');
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await api.adminCreatePrompt({
          code,
          body: text,
          model: model.trim(),
          reasoningEffort: effort,
          note: note.trim() || undefined,
        }),
      );
    } catch (err) {
      setError(adminErrorText(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="surface" padding="md" className="mb-5">
      <label className="block text-sm font-medium mb-1" htmlFor="prompt-body">
        {t.adminPrompts.text}
      </label>
      {base !== null && baseBody.isLoading && body === null ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <Textarea
          id="prompt-body"
          ref={bodyRef}
          value={text}
          onChange={(e) => setBody(e.target.value)}
          rows={16}
          className="font-mono text-xs"
        />
      )}

      <p className="text-xs text-hint mt-2 mb-1">{t.adminPrompts.placeholders}</p>
      <div className="flex flex-wrap gap-1 mb-4">
        {PROMPT_VARS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => insert(name)}
            className="px-2 py-1 rounded-lg bg-surface-2 text-xs font-mono break-all text-left"
          >
            {`{{${name}}} → ${vars[name]}`}
          </button>
        ))}
      </div>

      <label className="block text-sm font-medium mb-1" htmlFor="prompt-model">
        {t.adminPrompts.model}
      </label>
      <input
        id="prompt-model"
        className={cn(inputClass, 'font-mono mb-2')}
        value={model}
        autoCapitalize="off"
        autoCorrect="off"
        onChange={(e) => {
          setModel(e.target.value);
          setCheck(null);
        }}
      />

      <label className="block text-sm font-medium mb-1" htmlFor="prompt-effort">
        {t.adminPrompts.effort}
      </label>
      <select
        id="prompt-effort"
        className={cn(inputClass, 'mb-3')}
        value={effort ?? ''}
        disabled={code === 'transcribe'}
        onChange={(e) => {
          setEffort(e.target.value === '' ? null : (e.target.value as ReasoningEffortSetting));
          setCheck(null);
        }}
      >
        <option value="">{t.adminPrompts.effortNone}</option>
        {REASONING_EFFORTS.map((e) => (
          <option key={e} value={e}>
            {e}
          </option>
        ))}
      </select>

      <Button variant="secondary" size="sm" loading={checking} disabled={!model.trim()} onClick={checkModel}>
        {t.adminPrompts.checkModel}
      </Button>
      {check && (
        <Toast tone={check === 'ok' ? 'success' : 'warning'} className="mt-2">
          {t.adminPrompts.check[check]}
        </Toast>
      )}

      <label className="block text-sm font-medium mt-4 mb-1" htmlFor="prompt-note">
        {t.adminPrompts.note}
      </label>
      <input
        id="prompt-note"
        className={cn(inputClass, 'mb-4')}
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
      />

      {error && (
        <Toast tone="destructive" className="mb-3">
          {error}
        </Toast>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" fullWidth onClick={onCancel}>
          {t.adminPrompts.cancel}
        </Button>
        <Button variant="ink" fullWidth loading={saving} disabled={!model.trim()} onClick={save}>
          {t.adminPrompts.save}
        </Button>
      </div>
    </Card>
  );
}
